import { useCallback, useEffect, useRef, useState } from "react";

import useLanguage from "../../hooks/useLanguage";
import useContent from "../../hooks/useContent";
import useToaster from "../../hooks/useToaster";
import useDrawer from "../../hooks/useDrawer";
import MarkdownEditor from "../../components/MarkdownEditor";
import EditorSkeleton from "./skeleton";
import CommonPage from "../../components/common-page";
import EditorDrawer from "./EditorDrawer";
import IButton from "../../components/iconButton";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import PublishRoundedIcon from "@mui/icons-material/PublishRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import AssistantPanel from "./components/AssistantPanel";
import { applyEditsTo } from "./components/edits";
import { BodyWrapper, EditorColumn } from "./style";
import { notifyParent } from "../../utils/notifyParent";

const END_OF_FILE = "\nX-----EOF-----X\n";

export default function Editor() {
  const [
    {
      code,
      pages: { editor: text },
    },
  ] = useLanguage();
  const { open, setElement } = useDrawer();
  const { addToaster } = useToaster();
  const { getContentById, updateContent, recordDownload, setPublished } = useContent();
  const [form, setForm] = useState({
    id: "",
    title: "",
    tags: [],
    type: "",
    created: "",
    content: [],
    metadata: {
      banner: "",
      description: "",
      score: "",
      length: "",
    },
  });
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(true);
  // Snapshot of last persisted content/form, used to detect unsaved changes
  // when the in-editor Publish button needs to decide whether to auto-save
  // before flipping the published flag.
  const [savedSnapshot, setSavedSnapshot] = useState({ content: "", form: null });
  // Re-read the article id from the URL on every navigation (not just at mount) —
  // otherwise a persisted editor (Reshet WP iframe) keeps showing the first-loaded
  // article ("always the last one") when the parent switches which article to edit.
  const [contentId, setContentId] = useState(
    () => window.location.pathname.split("/")[2],
  );
  useEffect(() => {
    const sync = () => setContentId(window.location.pathname.split("/")[2]);
    window.addEventListener("locationchange", sync);
    return () => window.removeEventListener("locationchange", sync);
  }, []);

  const [selection, setSelection] = useState("");
  // MDXEditor only reads its `markdown` prop at mount, so applying an AI edit
  // has to go through the editor's imperative setMarkdown. The ref mirror of
  // `content` lets a batch of edits apply against the current text in one pass
  // without waiting for a state flush between them.
  const editorApiRef = useRef(null);
  const contentRef = useRef("");

  const handleChange = (form) => setForm(form);

  // Stable identity: MarkdownEditor's applyMarkdown (and every callback that
  // depends on it) is rebuilt whenever this one changes — per keystroke.
  const handleContentChange = useCallback((content) => {
    contentRef.current = content;
    setContent(content);
  }, []);

  const handleApplyEdits = useCallback((edits) => {
    const { text: next, applied } = applyEditsTo(contentRef.current, edits);
    if (!applied.length) return [];
    contentRef.current = next;
    setContent(next);
    editorApiRef.current?.applyMarkdown(next);
    return applied;
  }, []);

  const clearSelection = useCallback(() => setSelection(""), []);

  const fetchContent = async () => {
    try {
      setLoading(true);
      const response = await getContentById(contentId);
      if (!response?.content?.length) throw new Error(text.toaster.length);
      const joined = response.content?.join(END_OF_FILE);
      handleContentChange(joined);
      setForm(response);
      setSavedSnapshot({ content: joined, form: response });
      setLoading(false);
    } catch (error) {
      console.error(error);
      addToaster({
        message: error || error?.message || text.toaster.error,
        severity: "error",
      });
    }
  };

  const handleSubmit = async () => {
    if (!contentId)
      return addToaster({ message: text.toaster.contentId, severity: "error" });
    try {
      setLoading(true);
      open(false);
      const response = await updateContent(contentId, {
        ...form,
        content: content.split(END_OF_FILE),
      });
      setSavedSnapshot({ content, form });
      // Edits to already-published articles count as another publish event
      // for embedded contexts (Reshet WP). Pre-publish drafting stays silent.
      if (form?.published === true) notifyParent("ITEM_UPDATED", contentId);
      addToaster({
        message: response?.message || text.toaster.success,
        severity: "success",
      });
    } catch (error) {
      console.log(error);
      addToaster({
        message: error || text.toaster.error,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Auto-save (if dirty) then publish. Single ITEM_APPROVED postMessage,
  // intentionally no ITEM_UPDATED — this is the publish event, not an edit.
  const handlePublish = async () => {
    if (!contentId)
      return addToaster({ message: text.toaster.contentId, severity: "error" });
    try {
      setLoading(true);
      open(false);
      const isDirty =
        content !== savedSnapshot.content ||
        JSON.stringify(form) !== JSON.stringify(savedSnapshot.form);
      if (isDirty) {
        await updateContent(contentId, {
          ...form,
          content: content.split(END_OF_FILE),
        });
        setSavedSnapshot({ content, form });
      }
      await setPublished(contentId, true);
      setForm((prev) => ({ ...prev, published: true }));
      notifyParent("ITEM_APPROVED", contentId);
      addToaster({
        message: text.toaster.publishSuccess,
        severity: "success",
      });
    } catch (error) {
      console.log(error);
      addToaster({
        message: error || text.toaster.publishFailed,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    const markdown = (content || "").split(END_OF_FILE).join("\n");
    const blob = new Blob([markdown], { type: "text/markdown" });
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = `${form?.title || form?.id || contentId || "article"}.md`;
    a.click();
    URL.revokeObjectURL(blobUrl);
    try {
      if (contentId) await recordDownload(contentId);
    } catch {
      // toaster already raised by hook
    }
  };

  const handleDrawerClose = () => {
    setElement(null);
    open(false);
  };

  const handleDrawerOpen = () => {
    setElement(
      <EditorDrawer
        form={form}
        onChange={handleChange}
        onClose={handleDrawerClose}
      />
    );
    open(true);
  };

  // Load whenever the resolved id changes (mount + in-app/iframe navigation).
  useEffect(() => {
    if (contentId) fetchContent();
    else setLoading(false);
    open(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentId]);

  if (loading) return <EditorSkeleton />;

  return (
    <CommonPage
      title={text.title}
      subtitle={text.subtitle}
      // Full-bleed: the assistant takes a fixed column, so the article should
      // get the rest of the window rather than a centred 1200px slice of it.
      maxWidth={false}
      actions={[
        <IButton
          key="metadata"
          onClick={handleDrawerOpen}
          variant="outlined"
          color="action"
          startIcon={<OpenInNewRoundedIcon />}
        >
          {text.metadata}
        </IButton>,
        <IButton
          key="download"
          onClick={handleDownload}
          variant="outlined"
          color="action"
          startIcon={<DownloadRoundedIcon />}
        >
          {text.download}
        </IButton>,
        <IButton
          key="save"
          onClick={handleSubmit}
          variant="contained"
          color="action"
          startIcon={<SaveRoundedIcon />}
        >
          {text.save}
        </IButton>,
        ...(form?.published
          ? []
          : [
              <IButton
                key="publish"
                onClick={handlePublish}
                variant="contained"
                color="success"
                startIcon={<PublishRoundedIcon />}
              >
                {text.publish}
              </IButton>,
            ]),
      ]}
    >
      <BodyWrapper>
        <EditorColumn>
          <MarkdownEditor
            value={content}
            onChange={handleContentChange}
            apiRef={editorApiRef}
            onSelect={setSelection}
          />
        </EditorColumn>
        <AssistantPanel
          contentId={contentId}
          markdown={content}
          selection={selection}
          onClearSelection={clearSelection}
          onApplyEdits={handleApplyEdits}
          text={text.ai}
          uiLanguage={code}
        />
      </BodyWrapper>
    </CommonPage>
  );
}
